import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connect } from 'react-redux';
import { syncActiveProductReviews } from '../../Actions/ShopActions';
import './ProductReviews.scss';
import { ReviewPhotoPanel } from '../Reviews/ReviewPhotoPanel';
import SegmentedControl from '../Shop/Elements/SegmentedControl';
import { ReviewGrid } from '../Reviews/ReviewGrid';

const ProductReviews = ({ product, isFetchingFull, reviewSummary, isFetchingReviews, syncActiveProductReviews }) => {
  React.useEffect(() => {
    if (product?.id && !reviewSummary && !isFetchingReviews) {
      syncActiveProductReviews(product.id);
    }
  }, [product?.id]);

  const [activeTab, setActiveTab] = React.useState('photos'); // 'photos' | 'reviews'

  const additionalClasses = { loading: isFetchingFull || isFetchingReviews };

  if (isFetchingFull || isFetchingReviews) {
    return <div className='loading-block' />;
  }

  if (!reviewSummary?.summary) return null;

  // Only show if there are decent number of reviews
  if (reviewSummary.totalReviews < 20) return null;

  return (
    <div className={cn('product-reviews-container', additionalClasses)}>
      <div className='section-header-container'>
        <h2 className='section-header'>What real customers are saying...</h2>
      </div>

      <div className='product-reviews-summary'>
        <div className='summary-section'>
          <h3>Summary from {reviewSummary.totalReviews} reviews</h3>
          <p className='summary-body'>{reviewSummary.summary.summary}</p>
          <div className='summary-source'>AI summary based on {reviewSummary.totalReviews} Thingtesting reviews.</div>
        </div>

        <div className='summary-divider' />

        <div className='strengths-section'>
          {reviewSummary?.summary?.strengths?.length ? (
            <>
              <h3>Why choose {product?.brand?.name || 'this product'}</h3>
              <ul className='reasons-list'>
                {reviewSummary.summary.strengths.map((strength, idx) => (
                  <li key={idx}>{strength}</li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </div>

      {reviewSummary?.reviewsWithMedia?.items?.length && reviewSummary?.reviews?.items?.length && (
        <div className='media-section'>
          <div className='media-section-header'>
            <SegmentedControl
              tabOptions={[
                {
                  label: 'Photos',
                  mobileLabel: 'Photos',
                  isActive: activeTab === 'photos',
                  onClick: () => setActiveTab('photos')
                },
                {
                  label: 'Reviews',
                  mobileLabel: 'Reviews',
                  isActive: activeTab === 'reviews',
                  onClick: () => setActiveTab('reviews')
                }
              ]}
            />
          </div>

          {activeTab === 'photos' && (
            <ReviewPhotoPanel reviews={reviewSummary.reviewsWithMedia.items} totalReviews={reviewSummary.reviewsWithMedia.total} />
          )}
          {activeTab === 'reviews' && <ReviewGrid reviews={reviewSummary.reviews.items} />}
        </div>
      )}
    </div>
  );
};

ProductReviews.propTypes = {
  product: PropTypes.object,
  isFetchingFull: PropTypes.bool.isRequired,
  reviewSummary: PropTypes.object,
  isFetchingReviews: PropTypes.bool,
  syncActiveProductReviews: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { shop } = state;
  return {
    reviewSummary: shop.activeProductReviews,
    isFetchingReviews: shop.isFetchingProductReviews
  };
};

export default connect(mapStateToProps, { syncActiveProductReviews })(ProductReviews);
